import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { calcEMI, formatPriceINR } from "../lib/format";

const EMICalculator = () => {
  const [params] = useSearchParams();
  const initialPrice = Number(params.get("price")) || 3000000;
  const [price, setPrice] = useState(initialPrice);
  const [down, setDown] = useState(Math.round(initialPrice * 0.2));
  const [rate, setRate] = useState(9.5);
  const [tenure, setTenure] = useState(60);

  useEffect(() => {
    if (initialPrice) {
      setPrice(initialPrice);
      setDown(Math.round(initialPrice * 0.2));
    }
  }, [initialPrice]);

  const loan = Math.max(0, Number(price) - Number(down));
  const { emi, total, interest } = useMemo(() => calcEMI(loan, rate, tenure), [loan, rate, tenure]);

  return (
    <div className="container-wide py-16 max-w-5xl mx-auto" data-testid="page-emi">
      <div className="overline text-[#7A1519]">Finance</div>
      <h1 className="font-display text-4xl md:text-6xl mt-3 font-semibold tracking-tighter">EMI Calculator</h1>
      <p className="mt-4 text-[#242424]/75 max-w-2xl">An indicative monthly payment estimate. Actual finance terms depend on lender approval, rates and customer eligibility.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border hairline p-8 rounded-sm space-y-6">
          <Slider label="Vehicle Price" value={price} setValue={setPrice} min={200000} max={20000000} step={50000} format={formatPriceINR} testId="emi-price" />
          <Slider label="Down Payment" value={down} setValue={setDown} min={0} max={price} step={10000} format={formatPriceINR} testId="emi-down" />
          <Slider label="Interest Rate (p.a.)" value={rate} setValue={setRate} min={5} max={18} step={0.1} format={(v) => `${v.toFixed(1)}%`} testId="emi-rate" />
          <Slider label="Tenure (months)" value={tenure} setValue={setTenure} min={12} max={84} step={6} format={(v) => `${v} months`} testId="emi-tenure" />
        </div>

        <div className="bg-[#111111] text-white p-8 rounded-sm flex flex-col justify-between">
          <div>
            <div className="overline text-white/50">Estimated monthly EMI</div>
            <div className="mt-2 font-display text-5xl md:text-6xl tracking-tighter font-bold text-[#7A1519]">
              {formatPriceINR(emi)}<span className="text-lg text-white/60 font-normal"> / mo</span>
            </div>
          </div>
          <div className="mt-8 divide-y divide-white/10">
            <Line label="Loan Amount" value={formatPriceINR(loan)} />
            <Line label="Total Interest" value={formatPriceINR(interest)} />
            <Line label="Total Payment" value={formatPriceINR(total + Number(down))} />
          </div>
          <p className="mt-6 text-xs text-white/50 leading-relaxed">These calculations are indicative. Autobahn Cars' finance partners will share the final terms after verification.</p>
        </div>
      </div>
    </div>
  );
};

const Slider = ({ label, value, setValue, min, max, step, format, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="overline text-[#242424]/60">{label}</label>
      <div className="font-display font-semibold text-[#111111]">{format(value)}</div>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => setValue(Number(e.target.value))} className="w-full accent-[#7A1519]" data-testid={testId} />
    <div className="flex justify-between text-[10px] text-[#242424]/50 mt-1">
      <span>{format(min)}</span><span>{format(max)}</span>
    </div>
  </div>
);

const Line = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 text-sm">
    <span className="text-white/70">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

export default EMICalculator;
